import {
  partPopulationZone,
  REPARTITION_CLASSES_PAMI,
} from "@convex/referentiel/fourchette";

const PART_MAX = Math.max(...REPARTITION_CLASSES_PAMI);

const pourcent = (valeur: number) =>
  `${valeur.toLocaleString("fr-FR", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })} %`;

/**
 * Cloche de l'étalonnage PAMI en 11 classes (même figure que le rapport), avec
 * la zone cible du référent mise en avant et la part de la population qu'elle
 * couvre. Sans zone (attente pas encore choisie, ou « pas d'attente
 * particulière »), la cloche reste neutre : toutes les barres en gris, aucun
 * pourcentage. Elle reste affichée pour que la carte ne change pas de hauteur
 * d'une option à l'autre.
 *
 * Des barres en HTML plutôt qu'en SVG : la grille de 11 colonnes aligne barres
 * et scores sans calcul de coordonnées, et reste nette à toutes les largeurs.
 */
export function RepartitionZone({
  zone,
  sansAttente = false,
}: {
  zone: { min: number; max: number } | undefined;
  /** « Pas d'attente particulière » : aucune zone, et la légende le dit. */
  sansAttente?: boolean;
}) {
  const dansZone = (classe: number) =>
    zone !== undefined && classe >= zone.min && classe <= zone.max;

  return (
    <figure className="mx-auto w-full max-w-sm">
      <div aria-hidden className="grid grid-cols-11 gap-1">
        <div className="col-span-11 grid h-16 grid-cols-subgrid items-end">
          {REPARTITION_CLASSES_PAMI.map((part, classe) => (
            <div
              key={classe}
              title={`Score ${classe} : ${pourcent(part)} de la population`}
              className={`rounded-t-[3px] transition-colors duration-150 ${
                dansZone(classe) ? "bg-charte-accent-display" : "bg-charte-line-loud"
              }`}
              style={{ height: `${(part / PART_MAX) * 100}%` }}
            />
          ))}
        </div>
        <div className="col-span-11 grid grid-cols-subgrid border-t border-charte-line-loud pt-1">
          {REPARTITION_CLASSES_PAMI.map((_, classe) => (
            <span
              key={classe}
              className={`text-center text-xs tabular-nums ${
                dansZone(classe)
                  ? "font-semibold text-charte-strong"
                  : "text-charte-dim"
              }`}
            >
              {classe}
            </span>
          ))}
        </div>
      </div>
      <figcaption className="mt-2 text-center text-sm text-charte-muted">
        {sansAttente ? (
          "Pas d'attente particulière : la dimension ne compte pas dans le score."
        ) : zone === undefined ? (
          "Choisissez une attente pour situer la zone cible dans la population de référence."
        ) : (
          <>
            <span className="font-semibold tabular-nums text-charte-strong">
              {pourcent(partPopulationZone(zone))}
            </span>{" "}
            de la population de référence a un score dans cette zone.
          </>
        )}
      </figcaption>
    </figure>
  );
}
